// src/services/storage-service.js
// Centralized Firebase Storage service for all image uploads

import { storage } from '../config/firebase-config';
import { ref, uploadBytes, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

/**
 * Get the current authenticated user's ID
 */
const getCurrentUserId = () => {
  const auth = getAuth();
  return auth.currentUser?.uid || null;
};

/**
 * Upload a File object to Firebase Storage
 * @param {string} path - Storage path relative to practitioner folder (e.g., 'gallery/img_001.jpg')
 * @param {File} file - The file to upload
 * @returns {Promise<{url: string, fullPath: string}>} Download URL and full storage path
 */
export const uploadFile = async (path, file) => {
  const userId = getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  if (!storage) throw new Error('Firebase Storage is not initialized');

  const fullPath = `practitioners/${userId}/${path}`;
  const storageRef = ref(storage, fullPath);

  const metadata = {
    contentType: file.type,
    customMetadata: {
      uploadedAt: new Date().toISOString(),
    },
  };

  const snapshot = await uploadBytes(storageRef, file, metadata);
  const url = await getDownloadURL(snapshot.ref);

  console.log(`✅ Uploaded to Storage: ${fullPath}`);
  return { url, fullPath };
};

/**
 * Upload a Base64 data URL string to Firebase Storage
 * Used for migrating existing Base64 images and for processed images (resized/compressed)
 * @param {string} path - Storage path relative to practitioner folder
 * @param {string} base64DataUrl - Full data URL (e.g., 'data:image/jpeg;base64,...')
 * @returns {Promise<{url: string, fullPath: string}>} Download URL and full storage path
 */
export const uploadBase64 = async (path, base64DataUrl) => {
  const userId = getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  if (!storage) throw new Error('Firebase Storage is not initialized');

  const fullPath = `practitioners/${userId}/${path}`;
  const storageRef = ref(storage, fullPath);

  const snapshot = await uploadString(storageRef, base64DataUrl, 'data_url');
  const url = await getDownloadURL(snapshot.ref);

  console.log(`✅ Uploaded Base64 to Storage: ${fullPath}`);
  return { url, fullPath };
};

/**
 * Delete a file from Firebase Storage
 * @param {string} fullPath - Full storage path (e.g., 'practitioners/abc123/gallery/img_001.jpg')
 */
export const deleteFile = async (fullPath) => {
  if (!fullPath) return;
  if (!storage) return;

  try {
    const storageRef = ref(storage, fullPath);
    await deleteObject(storageRef);
    console.log(`✅ Deleted from Storage: ${fullPath}`);
  } catch (error) {
    // File may already be deleted or not exist
    if (error.code === 'storage/object-not-found') {
      console.warn(`⚠️ File not found in Storage (already deleted?): ${fullPath}`);
    } else {
      throw error;
    }
  }
};

/**
 * Get download URL for an existing file
 * @param {string} fullPath - Full storage path
 * @returns {Promise<string>} Download URL
 */
export const getFileURL = async (fullPath) => {
  const storageRef = ref(storage, fullPath);
  return await getDownloadURL(storageRef);
};

/**
 * Check if a string is a Firebase Storage URL (vs Base64)
 * Useful for backward compatibility during migration
 * @param {string} str - URL or Base64 string
 * @returns {boolean}
 */
export const isStorageURL = (str) => {
  if (!str || typeof str !== 'string') return false;
  return str.startsWith('https://firebasestorage.googleapis.com/')
      || str.startsWith('https://storage.googleapis.com/');
};

/**
 * Check if a string is a Base64 data URL
 * @param {string} str - URL or Base64 string
 * @returns {boolean}
 */
export const isBase64 = (str) => {
  if (!str || typeof str !== 'string') return false;
  return str.startsWith('data:image/');
};

/**
 * Generate a unique filename with timestamp
 * @param {string} originalName - Original file name
 * @returns {string} Unique filename
 */
export const generateFileName = (originalName) => {
  const timestamp = Date.now();
  const ext = originalName.split('.').pop().toLowerCase();
  const safeName = originalName
    .replace(/\.[^/.]+$/, '')      // Remove extension
    .replace(/[^a-zA-Z0-9]/g, '_') // Replace special chars
    .substring(0, 30);             // Limit length
  return `${timestamp}_${safeName}.${ext}`;
};
